// @flow
import { times, get } from 'lodash';
import { batch } from 'react-redux';

import createAction from 'Store/utils/createAction';
import { setKeywords } from 'Store/search/query/searchQuery.action';
import { keywordsSelector } from 'Store/search/search.selector';
import { DEFAULT_KID_VALUE, type ValidationState } from 'Store/trip-information/kids/constants';
import { setKids } from 'Store/trip-information/kids/kids.action';
import { setDepartureDate } from 'Store/trip-criteria/departure-date/departureDate.action';
import { setDepartureCity } from 'Store/trip-criteria/departure-city/departureCity.action';
import { setDuration } from 'Store/trip-criteria/duration/duration.action';
import { DEFAULT_NO_DURATION } from 'Store/trip-criteria/duration/duration.constants';
import { getKids } from 'Store/trip-information/storeHelper';
import { searchQuerySelector } from './faceting.selector';

import {
  FACETING_CLEAR_FACETS,
  FACETING_SET_FILTERS,
  FACETING_SET_SEARCH_QUERY,
  FACETING_SET_INITIAL_PRICES,
  FACETING_SET_KIDS_VALIDATION_STATE,
  FACETING_RESET_REDIRECTION,
  FACETING_RESET_SEARCH,
  FACETING_TOGGLE_FACET_SELECTED,
  FACETING_SET_IS_DATE_REQUIRED,
  FACETING_SET_IS_SEARCH_ENABLED,
  FACETING_UPDATE_FILTERS,
} from './constants';

export type ParamsWithValue<T> = {
  appContext?: AppContext,
  options?: FacetingSearchOptions,
  value: T,
};

export type ToggleFacetParams = {
  categoryId: string,
  isSelected: boolean,
  value: boolean | string,
};

function setActiveFacet(categoryId: string, value: string | boolean, isSelected: boolean) {
  return createAction<ToggleFacetParams>(FACETING_TOGGLE_FACET_SELECTED, {
    categoryId,
    value,
    isSelected,
  });
}

export function toggleFacet({ categoryId, isSelected, value }: ToggleFacetParams) {
  return setActiveFacet(categoryId, value, isSelected);
}

export function changeKids(value: Array<Kid>) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const oldKids = getKids(getState());
    const newKids = value.map((kid, index) => ({
      ...kid,
      neolid: get(oldKids, `[${index}].neolid`),
    }));

    return dispatch(setKids(newKids));
  };
}

function _changeKidsSize(kids: Array<Kid>, newSize: number): Array<Kid> {
  const emptyKids: Array<Kid> = times(newSize - kids.length, () => ({ ...DEFAULT_KID_VALUE }));
  if (newSize < kids.length) {
    return kids.slice(0, newSize);
  }
  return [...kids, ...emptyKids];
}

// use this export when testing.
export const changeKidsNumberWith = (changeKidsFunc: *) =>
  function changeKidsNumber(value: number) {
    return (dispatch: ReduxDispatch, getState: GetReduxState) => {
      const kids: Array<Kid> = getKids(getState());
      const newKids: Array<Kid> = _changeKidsSize(kids, value);
      return dispatch(changeKidsFunc(newKids));
    };
  };
// use this export in app code.
export const changeKidsNumber = changeKidsNumberWith(changeKids);

export function changeDepartureCity(value: ?string) {
  return setDepartureCity({ value });
}

export function changeDepartureDate({ value }: { value: timestamp }) {
  return setDepartureDate(value);
}

export const clearDepartureDate = () => setDepartureDate(null);
export const setSearchQuery = (query: SearchQuery = { label: '' }) =>
  createAction<{ value: { label: string } }>(FACETING_SET_SEARCH_QUERY, { value: query });

export function clearTripDuration() {
  return setDuration(DEFAULT_NO_DURATION);
}

export function changeSearchQuery(value: SearchQuery) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const store = getState();
    const keywords = keywordsSelector(store);
    const searchQuery = searchQuerySelector(getState());

    batch(() => {
      if (keywords) {
        dispatch(setKeywords(''));
      }

      if (searchQuery.label !== value.label) {
        dispatch(setSearchQuery(value));
      }
    });
  };
}

export function changeTripDuration(value: ?number) {
  return setDuration(value);
}

export function setInitialProductPrices(
  initialProductPrices: $ReadOnlyArray<OfferProduct>,
): SetInitialProductPricesAction {
  return createAction(FACETING_SET_INITIAL_PRICES, { initialProductPrices });
}

export function setKidsValidationState(
  kidsValidationState: Array<ValidationState>,
): Action<{ kidsValidationState: Array<ValidationState> }> {
  return createAction(FACETING_SET_KIDS_VALIDATION_STATE, { kidsValidationState });
}

export function clearFacets(categoryId: string) {
  return createAction<{ categoryId: string }>(FACETING_CLEAR_FACETS, { categoryId });
}

export function resetRedirection() {
  return createAction<void>(FACETING_RESET_REDIRECTION);
}

export function resetSearch() {
  return async (dispatch: ReduxDispatch) => {
    // Reset faceting state
    return dispatch(createAction(FACETING_RESET_SEARCH));
  };
}

export function setFilters(filters: Array<ActiveFacet>) {
  return createAction(FACETING_SET_FILTERS, { filters });
}

export function updateFilters(filters: Array<ActiveFacet>) {
  return createAction(FACETING_UPDATE_FILTERS, { filters });
}

export function resetCriteria() {
  return async (dispatch: ReduxDispatch) => {
    return batch(() => {
      dispatch(setDepartureDate(null));
      dispatch(setDepartureCity({ value: null }));
      dispatch(setDuration(DEFAULT_NO_DURATION));
      dispatch(updateFilters([]));
      dispatch(setSearchQuery({ label: '' }));
    });
  };
}

export function setIsDateRequired(isDateRequired: boolean) {
  return createAction<{ isDateRequired: boolean }>(FACETING_SET_IS_DATE_REQUIRED, {
    isDateRequired,
  });
}

export function setIsSearchEnabled(isSearchEnabled: boolean) {
  return createAction<{ isSearchEnabled: boolean }>(FACETING_SET_IS_SEARCH_ENABLED, {
    isSearchEnabled,
  });
}
